/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Fetches images from authenticated proxy endpoints using session cookies,
 * converting them to blob URLs for use in <img> tags.
 * Non-proxy URLs are passed through unchanged.
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

function isProxyUrl(url) {
  return typeof url === "string" && url.includes("/organizations/s3/receipt-media/");
}

export function useCredentialedUrl(url) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!url || !isProxyUrl(url)) {
      setBlobUrl(null);
      return;
    }
    let revoke = null;
    fetch(url, { credentials: "include" })
      .then((res) => {
        if (!res.ok) return null;
        return res.blob();
      })
      .then((blob) => {
        if (blob) {
          const objUrl = URL.createObjectURL(blob);
          revoke = objUrl;
          setBlobUrl(objUrl);
        }
      })
      .catch(() => {});
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url]);
  return isProxyUrl(url) ? blobUrl : url;
}

export default function CredentialedImage({ src, alt, style }) {
  const resolvedSrc = useCredentialedUrl(src);
  if (!resolvedSrc) return null;
  return <img src={resolvedSrc} alt={alt} style={style} />;
}

CredentialedImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.object
};
